import { Icon, Text } from '@/components/atoms';
import APP_CONFIG from '@/config/app';
import { IconsName, IconVariants } from '@/interfaces';
import Image from 'next/image';
import React, {
  FC,
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const {
  MAIN_PADDING,
  MAIN_GAP,
  MAIN_ROUNDEDNESS,
  WHOLESTEP_DECREMENT,
  HALFSTEP_DECREMENT,
  TEXT: {
    SMALL_HEADING,
    SMALL_HEADING_COLOR,
    SMALL_SUBHEADING,
    SMALL_SUBHEADING_COLOR,
    BODY,
    BODY_COLOR,
  },
} = APP_CONFIG.STYLES;
const spacingConfig = {
  padding: `p${MAIN_PADDING}`,
  gap: `gap${MAIN_GAP}`,
  headerPadding: 'p-0',
  headerGap: `gap${MAIN_GAP}`,
  headerGapBetweenLeftIconAndGroupOfTitleAndSubHeaderGroup: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  headerGapBetweenGroupOfTitleAndSubHeaderAndBodyWhenBodyIsString: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  gapBetweenBodyElements: `gap${MAIN_GAP}${WHOLESTEP_DECREMENT}`,
  headerGapBetweenTitleAndSubHeader: `gap${MAIN_GAP}${HALFSTEP_DECREMENT}`,
};
const roundednessConfig = {
  roundedness: `rounded${MAIN_ROUNDEDNESS}`,
};
const sizingConfig = {
  width: 'w-full',
  iconSize: 'md' as IconVariants['size'],
};
const textConfig = {
  headerTitleSize: SMALL_HEADING,
  headerTitleColor: SMALL_HEADING_COLOR,
  subHeaderSize: SMALL_SUBHEADING,
  subHeaderColor: SMALL_SUBHEADING_COLOR,
  bodyTextSize: BODY,
  bodyTextColor: BODY_COLOR,
};
const colorConfig = {
  backgroundColor: 'bg-primary/5',
  borderColor: 'border border-primary/5',
  headerTextColor: 'text-primary',
  subHeaderTextColor: 'text-secondary',
  bodyTextColor: 'text-secondary',
};
interface SectionProps {
  headerTitle?: ReactNode;
  subHeader?: ReactNode;
  body: ReactNode | ReactNode[] | string;
  icon?: ReactElement | null;
  iconUrl?: string;
  iconName?: IconsName;
  iconIntent?: IconVariants['intent'];
  headerRightNode?: ReactNode;
  className?: string;
  shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader?: boolean;
  isBackgroundTransparent?: boolean;
  isFullBleed?: boolean;
  isVerticalOnlyBleed?: boolean;
}
const Section: FC<SectionProps> = ({
  headerTitle,
  subHeader,
  body,
  icon,
  iconUrl,
  iconName,
  iconIntent,
  headerRightNode,
  className,
  shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader = false,
  isBackgroundTransparent = false,
  isFullBleed = false,
  isVerticalOnlyBleed = false,
}) => {
  const [iconHeight, setIconHeight] = useState<number | null>(null);
  const textGroupRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (
      shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader &&
      textGroupRef.current
    ) {
      const height = textGroupRef.current.clientHeight;
      setIconHeight(height);
    }
  }, [
    shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader,
    headerTitle,
    subHeader,
  ]);
  const renderLeftIcon = () => {
    if (icon) {
      return React.cloneElement(icon, {
        className: `${sizingConfig.iconSize}`,
        style:
          shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader &&
          iconHeight
            ? { width: `${iconHeight}px`, height: `${iconHeight}px` }
            : {},
      });
    }
    if (iconUrl) {
      return (
        <Image
          src={iconUrl}
          alt="icon"
          width={
            shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader &&
            iconHeight
              ? iconHeight
              : 36
          }
          height={
            shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader &&
            iconHeight
              ? iconHeight
              : 36
          }
          className={`flex items-start ${sizingConfig.iconSize}`}
        />
      );
    }
    if (iconName) {
      return (
        <Icon
          name={iconName}
          intent={iconIntent}
          size={
            !shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader
              ? sizingConfig.iconSize
              : undefined
          }
          sizeAsNumber={
            shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader
              ? iconHeight || 36
              : undefined
          }
          className={`flex items-start`}
        />
      );
    }
    return null;
  };
  const renderRightNode = () => {
    return headerRightNode ? (
      <div
        className={`flex items-center ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {headerRightNode}
      </div>
    ) : null;
  };
  const leftIconElement = renderLeftIcon();
  const rightNodeElement = renderRightNode();
  return (
    <div
      className={`flex flex-col  
        ${sizingConfig.width} 
        ${spacingConfig.gap} 
        ${sizingConfig.iconSize} 
        ${
          isBackgroundTransparent
            ? 'bg-transparent'
            : colorConfig.backgroundColor
        }
        ${isBackgroundTransparent ? 'border-none' : colorConfig.borderColor}
        ${
          isFullBleed
            ? 'p-0'
            : isVerticalOnlyBleed
            ? `px${MAIN_PADDING}`
            : spacingConfig.padding
        }
        ${roundednessConfig.roundedness} 
        ${className} 
        ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {(headerTitle || leftIconElement) && (
        <div
          className={`flex justify-between items-center 
            ${spacingConfig.headerPadding} 
            ${spacingConfig.headerGap}`}
        >
          <div
            className={`flex ${
              shouldIconHeightEqualHeightOfGroupOfHeaderTitleAndSubheader
                ? 'items-center'
                : 'items-start'
            }
            ${
              spacingConfig.headerGapBetweenLeftIconAndGroupOfTitleAndSubHeaderGroup
            } 
            ${borderClassToVisualizeElementsAndSpacing}`}
          >
            {leftIconElement}
            <div
              ref={textGroupRef}
              className={`flex flex-col justify-start 
                ${spacingConfig.headerGapBetweenGroupOfTitleAndSubHeaderAndBodyWhenBodyIsString} 
                ${borderClassToVisualizeElementsAndSpacing}`}
            >
              <div
                className={`flex flex-col justify-start 
                  ${spacingConfig.headerGapBetweenTitleAndSubHeader} 
                  ${borderClassToVisualizeElementsAndSpacing}`}
              >
                {headerTitle &&
                  (typeof headerTitle === 'string' ? (
                    <Text
                      value={headerTitle}
                      color={textConfig.headerTitleColor}
                      size={textConfig.headerTitleSize}
                    />
                  ) : (
                    headerTitle
                  ))}
                {subHeader &&
                  (typeof subHeader === 'string' ? (
                    <Text
                      value={subHeader}
                      color={textConfig.subHeaderColor}
                      size={textConfig.subHeaderSize}
                      className={borderClassToVisualizeElementsAndSpacing}
                    />
                  ) : (
                    subHeader
                  ))}
              </div>
              {typeof body === 'string' && (
                <Text
                  value={body}
                  color={textConfig.bodyTextColor}
                  size={textConfig.bodyTextSize}
                  className={borderClassToVisualizeElementsAndSpacing}
                />
              )}
            </div>
          </div>
          {rightNodeElement}
        </div>
      )}
      {Array.isArray(body) ? (
        <div
          className={`flex flex-col ${spacingConfig.gapBetweenBodyElements}`}
        >
          {body.map((element, index) => (
            <div
              key={index}
              className={borderClassToVisualizeElementsAndSpacing}
            >
              {element}
            </div>
          ))}
        </div>
      ) : (
        typeof body !== 'string' && (
          <div
            className={`flex flex-col ${borderClassToVisualizeElementsAndSpacing}`}
          >
            {body}
          </div>
        )
      )}
    </div>
  );
};
export default Section;
